import { useRef, useCallback } from 'react';
import { hapticSelection } from '../../utils/haptics';

/**
 * PresetCarousel — vertical touch-swipe carousel for video presets.
 *
 * Figma: 363:3469 (preset selector) + 363:4752 / 363:4881 / 569:8338
 * (preset variants — these don't change the carousel itself, only the
 * underlying ReferenceSlot / quality state which lives in VideoGenerate).
 *
 * Handoff §9: «свайп вверх/вниз → следующий/предыдущий preset».
 *
 * State stays in the parent (VideoGenerate). This component is gesture-only:
 * it computes the new index and calls `onSwipe(newIndex)`. The parent
 * navigate()s to /video-generate/:newId with `replace: true`. The component
 * remains mounted across navigate() (same route, only URL param differs),
 * so VideoGenerate's local state for aspectRatio / quality / referenceUrl
 * survives the swipe.
 *
 * Gesture rules:
 *  - swipe UP    (finger moves up,   endY < startY, delta < -threshold) → next
 *  - swipe DOWN  (finger moves down, endY > startY, delta > +threshold) → prev
 *  - |delta| < threshold                                                 → no-op
 *  - currentIndex at boundary                                            → no-op
 *  - empty presets list                                                  → no-op
 *  - one swipe per gesture (we only react on touchEnd, never on touchMove)
 *
 * Props:
 *  - presets      (Array<{id: string}>) — flat list of all video presets
 *  - currentIndex (number)              — current preset's position in `presets`
 *  - onSwipe      (fn(newIndex))        — called when swipe crosses threshold
 *  - threshold    (number, default=50)  — px Y-delta required to trigger swipe
 *  - children     (ReactNode)           — visual content (background image etc.)
 */
export default function PresetCarousel({
  presets = [],
  currentIndex = 0,
  onSwipe,
  threshold = 50,
  children,
}) {
  const touchStartY = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartY.current = t.clientY;
  }, []);

  const handleTouchMove = useCallback(() => {
    // intentionally empty — only end-of-gesture is meaningful
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      if (touchStartY.current == null) return;
      const endY = e.changedTouches?.[0]?.clientY;
      if (endY == null) {
        touchStartY.current = null;
        return;
      }
      const delta = endY - touchStartY.current;
      touchStartY.current = null;

      if (Math.abs(delta) < threshold) return;
      if (typeof onSwipe !== 'function') return;
      if (!Array.isArray(presets) || presets.length === 0) return;

      // swipe UP → finger moves up → endY < startY → delta < 0 → next preset
      if (delta < 0 && currentIndex < presets.length - 1) {
        hapticSelection();
        onSwipe(currentIndex + 1);
      } else if (delta > 0 && currentIndex > 0) {
        hapticSelection();
        onSwipe(currentIndex - 1);
      }
    },
    [currentIndex, onSwipe, presets, threshold]
  );

  return (
    <div
      className="relative min-h-0 flex-1 w-full overflow-hidden"
      data-testid="preset-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {children}
    </div>
  );
}
